import React, { useEffect, useState, useCallback } from 'react';
import { Table, Input, Modal, Form, message, Upload, Button, Breadcrumb, Grid, Card, Row, Col, Statistic, Avatar, Tag, Tooltip, Dropdown, Switch, DatePicker, Select, Space } from 'antd';
import { getCategories, citiesService } from '../../common/services';
import {
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  RightOutlined,
  SearchOutlined,
  AppstoreOutlined,
  CalendarOutlined,
  PictureOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  PlusOutlined,
  CheckOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

dayjs.extend(customParseFormat);

// Formater la date
const formatDate = (date) => {
  if (!date) return '-';
  const formats = [
    'YYYY-MM-DDTHH:mm:ss.SSSZ',
    'YYYY-MM-DD HH:mm:ss',
    'DD/MM/YYYY HH:mm:ss',
    'DD/MM/YYYY HH:mm',
    'DD/MM/YYYY',
  ];
  let parsed = dayjs(date);
  if (!parsed.isValid()) {
    for (const fmt of formats) {
      parsed = dayjs(date, fmt);
      if (parsed.isValid()) break;
    }
  }
  if (parsed.isValid()) {
    return parsed.format('DD/MM/YYYY HH:mm');
  }
  if (typeof date === 'string') return date;
  return '-';
};

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    thisMonth: 0,
    thisWeek: 0
  });

  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [selectedCategorie, setSelectedCategorie] = useState(null);
  const [cities, setCities] = useState([]);
  const [form] = Form.useForm();

  useEffect(() => {
    (async () => {
      try {
        const res = await citiesService.getAll();
        setCities(res?.cities || res?.data || res || []);
      } catch (e) { /* silencieux */ }
    })();
  }, []);

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const controlSize = isMobile ? 'small' : 'middle';

  // Calcul des statistiques
  const calculateStats = useCallback((data) => {
    const startOfMonth = dayjs().startOf('month');
    const startOfWeek = dayjs().startOf('week');

    const monthCount = data.filter(item => {
      const itemDate = dayjs(item.createdAt);
      return itemDate.isValid() && itemDate.isAfter(startOfMonth);
    }).length;

    const weekCount = data.filter(item => {
      const itemDate = dayjs(item.createdAt);
      return itemDate.isValid() && itemDate.isAfter(startOfWeek);
    }).length;

    setStats({
      total: data.length,
      thisMonth: monthCount,
      thisWeek: weekCount
    });
  }, []);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = () => {
    setLoading(true);
    getCategories
      .get()
      .then((data) => {
        const categoriesData = Array.isArray(data) ? data : [];
        setCategories(categoriesData);
        calculateStats(categoriesData);
      })
      .catch((err) => {
        console.error('Erreur lors du chargement des catégories :', err);
        message.error('Erreur lors du chargement des catégories');
      })
      .finally(() => setLoading(false));
  };

  const filteredCategories = categories.filter((categorie) => {
    if (!categorie.name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (dateRange && dateRange[0] && dateRange[1]) {
      const created = dayjs(categorie.createdAt);
      return created.isAfter(dateRange[0].startOf('day').subtract(1, 'ms')) &&
        created.isBefore(dateRange[1].endOf('day').add(1, 'ms'));
    }
    return true;
  });

  const handleEdit = (record) => {
    setSelectedCategorie(record);
    form.setFieldsValue({
      name: record.name,
      image: record.image,
      villes: (record.villes || []).map((v) => v?._id || v),
    });
    setEditModalOpen(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar
            size={40}
            style={{ backgroundColor: '#ff4d4f' }}
            icon={<DeleteOutlined />}
          />
          <span>Supprimer la catégorie</span>
        </div>
      ),
      content: (
        <Card
          size="small"
          style={{ marginTop: 16, background: '#fff1f0', border: '1px solid #ffa39e' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img
              src={record.image?.replace(/\\/g, '/')}
              alt={record.name}
              style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 8 }}
            />
            <div>
              <p style={{ marginBottom: 0, fontWeight: 500 }}>{record.name}</p>
              <p style={{ marginBottom: 0, fontSize: 12, color: '#8c8c8c' }}>Cette action est irréversible</p>
            </div>
          </div>
        </Card>
      ),
      okText: 'Oui, supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      centered: true,
      width: isMobile ? 360 : 480,
      okButtonProps: { danger: true, size: controlSize },
      cancelButtonProps: { size: controlSize },
      onOk: async () => {
        try {
          await getCategories.delete(record._id);
          message.success('Catégorie supprimée avec succès');
          fetchCategories();
        } catch (error) {
          console.error('Erreur suppression :', error);
          message.error("Erreur lors de la suppression");
        }
      },
    });
  };

  const handleEditSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!selectedCategorie) return;

      const formData = new FormData();
      formData.append("name", values.name);
      // Zonage : toujours envoyé (vide = partout)
      formData.append("villes", JSON.stringify(values.villes || []));

      const fileList = values.image?.fileList;
      if (fileList && fileList.length > 0) {
        const file = fileList[0].originFileObj;
        formData.append("image", file);
      }

      await getCategories.put(selectedCategorie._id, formData);

      message.success("Catégorie mise à jour");
      setEditModalOpen(false);
      fetchCategories();
    } catch (error) {
      console.error("Erreur de mise à jour :", error);
      message.error("Erreur lors de la mise à jour");
    }
  };

  const handleToggle = async (record, checked) => {
    try {
      const response = await getCategories.toggle(record._id, checked);
      if (response?.success) {
        message.success(checked ? 'Service activé' : 'Service désactivé');
        fetchCategories();
      } else {
        message.error(response?.message || 'Erreur lors de la modification');
      }
    } catch (error) {
      console.error('Erreur de toggle:', error);
      message.error(error?.message || 'Erreur lors de la modification');
    }
  };

  // Export Excel
  const exportToExcel = () => {
    const hideLoading = message.loading('Export Excel en cours...', 0);
    try {
      const worksheet = XLSX.utils.json_to_sheet(filteredCategories.map(item => ({
        Nom: item.name,
        DateCreation: formatDate(item.createdAt),
      })));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Categories");
      XLSX.writeFile(workbook, "categories_services.xlsx");
      message.success(`${filteredCategories.length} catégories exportées`);
    } catch (e) {
      message.error('Erreur lors de l\'export Excel');
    } finally {
      hideLoading();
    }
  };

  // Export CSV
  const exportToCSV = () => {
    const hideLoading = message.loading('Export CSV en cours...', 0);
    try {
      const worksheet = XLSX.utils.json_to_sheet(filteredCategories.map(item => ({
        Nom: item.name,
        DateCreation: formatDate(item.createdAt),
      })));
      const csv = XLSX.utils.sheet_to_csv(worksheet);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'categories_services.csv');
      message.success(`${filteredCategories.length} catégories exportées`);
    } catch (e) {
      message.error('Erreur lors de l\'export CSV');
    } finally {
      hideLoading();
    }
  };

  // Export PDF
  const exportToPDF = () => {
    const hideLoading = message.loading('Export PDF en cours...', 0);
    try {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text("Liste des catégories de services", 14, 15);
      doc.setFontSize(10);
      doc.text(`Généré le ${dayjs().format('DD/MM/YYYY HH:mm')}`, 14, 22);

      autoTable(doc, {
        startY: 30,
        head: [['Nom', 'Date de création']],
        body: filteredCategories.map(item => [
          item.name,
          formatDate(item.createdAt)
        ]),
        styles: { fontSize: 10 },
        headStyles: { fillColor: [47, 84, 235] },
      });

      doc.save("categories_services.pdf");
      message.success(`${filteredCategories.length} catégories exportées`);
    } catch (e) {
      message.error('Erreur lors de l\'export PDF');
    } finally {
      hideLoading();
    }
  };

  // Menu dropdown pour export
  const exportMenuItems = [
    {
      key: 'excel',
      icon: <FileExcelOutlined style={{ color: '#52c41a' }} />,
      label: 'Exporter Excel (.xlsx)',
      onClick: exportToExcel
    },
    {
      key: 'csv',
      icon: <DownloadOutlined style={{ color: '#fa8c16' }} />,
      label: 'Exporter CSV',
      onClick: exportToCSV
    },
    {
      key: 'pdf',
      icon: <FilePdfOutlined style={{ color: '#ff4d4f' }} />,
      label: 'Exporter PDF',
      onClick: exportToPDF
    }
  ];

  const columns = [
    {
      title: 'Catégorie',
      key: 'categorie',
      width: isMobile ? 200 : 350,
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 60,
            height: 60,
            borderRadius: 12,
            overflow: 'hidden',
            border: '2px solid #f0f0f0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <img
              src={record.image?.replace(/\\/g, '/')}
              alt={record.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15, color: '#262626' }}>
              {record.name}
            </div>
            <Tag color="orange" style={{ marginTop: 4 }}>
              <AppstoreOutlined style={{ marginRight: 4 }} />
              Service
            </Tag>
          </div>
        </div>
      )
    },
    {
      title: 'Date de création',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      sorter: (a, b) => {
        const aDate = dayjs(a.createdAt);
        const bDate = dayjs(b.createdAt);
        return aDate.valueOf() - bDate.valueOf();
      },
      render: (date) => (
        <Tooltip title={formatDate(date)}>
          <span style={{ color: '#595959' }}>
            <CalendarOutlined style={{ marginRight: 6, color: '#fa8c16' }} />
            {formatDate(date)}
          </span>
        </Tooltip>
      )
    },
    {
      title: 'Zonage (villes)',
      key: 'villes',
      render: (_, record) => {
        const villes = record.villes || [];
        if (villes.length === 0) {
          return <Tag color="green">Partout</Tag>;
        }
        return (
          <Space size={[4, 4]} wrap>
            {villes.map((v) => (
              <Tag key={v?._id || v} color="blue">{v?.name || v?.code || '—'}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Statut',
      key: 'status',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Tooltip title={record.isActive !== false ? 'Actif - Cliquer pour désactiver' : 'Inactif - Cliquer pour activer'}>
          <Switch
            checked={record.isActive !== false}
            onChange={(checked) => handleToggle(record, checked)}
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            size="small"
            style={{
              backgroundColor: record.isActive !== false ? '#52c41a' : '#ff4d4f'
            }}
          />
        </Tooltip>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <Tooltip title="Modifier">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
              style={{ borderRadius: 6, color: '#faad14', borderColor: '#faad14' }}
            />
          </Tooltip>
          <Tooltip title="Supprimer">
            <Button
              danger
              type="text"
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDelete(record)}
              style={{ borderRadius: 6 }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  // Style des lignes alternées
  const rowClassName = (_, index) => {
    return index % 2 === 0 ? 'table-row-light' : 'table-row-dark';
  };

  return (
    <div className="content-wrapper">
      <style>
        {`
          .table-row-light {
            background-color: #ffffff;
          }
          .table-row-dark {
            background-color: #f0f5ff;
          }
          .ant-table-tbody > tr:hover > td {
            background-color: #e6f4ff !important;
          }
          .ant-table-thead > tr > th {
            background: #fafafa !important;
            color: #262626 !important;
            font-weight: 600 !important;
          }
          .stat-card-orange {
            background: linear-gradient(135deg, #fa8c16 0%, #ffc069 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-orange .ant-statistic-title {
            color: rgba(255,255,255,0.85) !important;
            font-size: 13px !important;
          }
          .stat-card-orange .ant-statistic-content {
            color: white !important;
          }
        `}
      </style>

      <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: 24, paddingRight: 24 }}>
        {/* Breadcrumb */}
        <Breadcrumb
          separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
          style={{ marginBottom: 12, marginTop: 0, paddingTop: 0 }}
        >
          <Breadcrumb.Item>
            <Link to="/dashboard" style={{ color: '#fa8c16' }}>Accueil</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/dashboard" style={{ color: '#fa8c16' }}>Gestion Services</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span style={{ color: '#595959' }}>Liste</span>
          </Breadcrumb.Item>
        </Breadcrumb>

        {/* Statistiques */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={8} sm={8} md={8}>
            <Card className="stat-card-orange" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><AppstoreOutlined style={{ marginRight: 6 }} />Total</span>}
                value={stats.total}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={8} sm={8} md={8}>
            <Card className="stat-card-orange" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><CalendarOutlined style={{ marginRight: 6 }} />Ce mois</span>}
                value={stats.thisMonth}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={8} sm={8} md={8}>
            <Card className="stat-card-orange" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><CalendarOutlined style={{ marginRight: 6 }} />Cette semaine</span>}
                value={stats.thisWeek}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Carte principale */}
        <Card
          style={{
            borderRadius: 16,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}
          bodyStyle={{ padding: isMobile ? 12 : 24 }}
        >
          {/* Filtres */}
          <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
            <Col xs={24} sm={10} md={8}>
              <Input
                prefix={<SearchOutlined style={{ color: '#fa8c16' }} />}
                placeholder="Rechercher par nom de catégorie..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
                size={controlSize}
                style={{ borderRadius: 8 }}
              />
            </Col>
            <Col xs={24} sm={8} md={8}>
              <DatePicker.RangePicker
                value={dateRange}
                onChange={(v) => setDateRange(v)}
                format="DD/MM/YYYY"
                placeholder={['Date début', 'Date fin']}
                style={{ width: '100%', borderRadius: 8 }}
                size={controlSize}
                allowClear
              />
            </Col>
            <Col xs={24} sm={6} md={8} style={{ display: 'flex', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
              <Dropdown
                menu={{ items: exportMenuItems }}
                placement="bottomRight"
                trigger={['click']}
              >
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  size={controlSize}
                  style={{
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, #fa8c16 0%, #ffc069 100%)',
                    border: 'none'
                  }}
                >
                  Exporter {!isMobile && `(${filteredCategories.length})`}
                </Button>
              </Dropdown>
            </Col>
          </Row>

          {/* Table */}
          <Table
            columns={columns}
            dataSource={filteredCategories.map((categorie) => ({
              ...categorie,
              key: categorie._id,
            }))}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, 50],
              showTotal: (t, range) => (
                <span style={{ color: '#8c8c8c' }}>
                  {range[0]}-{range[1]} sur {t} catégories
                </span>
              ),
              showLessItems: isMobile
            }}
            loading={isLoading}
            size={controlSize}
            scroll={{ x: 600 }}
            rowClassName={rowClassName}
          />
        </Card>
      </div>

      {/* Modal d'édition */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Avatar
              size={40}
              style={{ backgroundColor: '#fa8c16' }}
              icon={<EditOutlined />}
            />
            <span>Modifier la catégorie</span>
          </div>
        }
        open={isEditModalOpen}
        onCancel={() => setEditModalOpen(false)}
        onOk={handleEditSubmit}
        okText="Enregistrer"
        cancelText="Annuler"
        centered
        width={isMobile ? 360 : 520}
        okButtonProps={{
          size: controlSize,
          style: {
            background: 'linear-gradient(135deg, #fa8c16 0%, #ffc069 100%)',
            border: 'none'
          }
        }}
        cancelButtonProps={{ size: controlSize }}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label="Nom de la catégorie"
            name="name"
            rules={[{ required: true, message: 'Le nom est requis' }]}
          >
            <Input
              placeholder="Ex: Yango Taxi"
              style={{ borderRadius: 8 }}
            />
          </Form.Item>
          <Form.Item
            label="Villes (zonage)"
            name="villes"
            tooltip="Laisser vide = visible PARTOUT. Sinon, visible uniquement dans les villes sélectionnées."
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="Toutes les villes (laisser vide = partout)"
              style={{ width: '100%' }}
              optionFilterProp="label"
              options={cities.map((c) => ({ value: c._id, label: `${c.name}${c.code ? ` (${c.code})` : ''}` }))}
            />
          </Form.Item>
          <Form.Item label="Image" name="image">
            <Upload
              beforeUpload={() => false}
              maxCount={1}
              listType="picture-card"
            >
              <div>
                <PictureOutlined style={{ fontSize: 24, color: '#fa8c16' }} />
                <div style={{ marginTop: 8 }}>Changer l'image</div>
              </div>
            </Upload>
          </Form.Item>
          {selectedCategorie?.image && (
            <div style={{ marginBottom: 16 }}>
              <p style={{ marginBottom: 8, color: '#8c8c8c' }}>Image actuelle :</p>
              <img
                src={selectedCategorie.image?.replace(/\\/g, '/')}
                alt="Current"
                style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }}
              />
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default Categories;
